import classNames from 'classnames';
import { memo, useCallback, useState } from 'react';
import { Button } from '../../../components/Button';
import { Modal } from '../../../components/Modal';
import { CheckBox } from '../../../components/inputs/CheckBox';
import { Input } from '../../../components/inputs/Input';
import { ToastType, useToastNotifications } from '../../../hooks/useToastNotifications';
import { T, useT } from '../../../translation/src';
import { Project, UploadStatus, useSuperadminDebugSetProjectStateMutation } from '../../../types/graphqlTypes';
import { graphqlErrorCode } from '../../../utils/graphqlErrors';

interface ProjectDebugStateModalProps {
  onClose: () => void;
  open?: boolean;
  project: Pick<Project, 'id' | 'name' | 'uploadStatus' | 'creditsOutstanding' | 'creditsWaived' | 'creditBreakdown'>;
}

const STATUSES = [UploadStatus.Success, UploadStatus.AwaitingCredits, UploadStatus.Progress, UploadStatus.Error, UploadStatus.Suspended] satisfies UploadStatus[];

const POINT_PRESETS = [
  { label: '100M', points: 100_000_000 },
  { label: '1.5B', points: 1_500_000_000 },
  { label: '25B', points: 25_000_000_000 },
];

const parseCount = (value: string): number | null => {
  const parsed = Number(value.replace(/[\s_.,]/g, ''));
  return value.trim() !== '' && Number.isFinite(parsed) && parsed >= 0 ? parsed : null;
};

// Superadmin debug tool (behind "show debug ui"): dials a project into any state to try every flow without
// uploading anything. The server keeps what it overwrote in the project's audit log.
const ProjectDebugStateModal_: React.FC<ProjectDebugStateModalProps> = ({ onClose, open, project }) => {
  const [setState, { loading }] = useSuperadminDebugSetProjectStateMutation({ refetchQueries: ['organisations'] });
  const { showToast } = useToastNotifications();
  const failed = useT('could not set the project state', { swc: true });
  const noPointCloud = useT('the project has no point cloud to put the points on', { swc: true });

  const [uploadStatus, setUploadStatus] = useState<UploadStatus>(project.uploadStatus);
  const [pointCount, setPointCount] = useState(String(project.creditBreakdown.pointCount));
  // What is charged, as far as the card knows it: sent only when changed, so a larger real value is never clobbered.
  const initialCharged = project.creditBreakdown.required - project.creditsOutstanding;
  const [creditsCharged, setCreditsCharged] = useState(String(initialCharged));
  const [creditsWaived, setCreditsWaived] = useState(project.creditsWaived);

  const onApply = useCallback(async () => {
    const points = parseCount(pointCount);
    const charged = parseCount(creditsCharged);
    try {
      await setState({
        variables: {
          projectId: project.id,
          state: {
            uploadStatus,
            ...(points !== null && points !== project.creditBreakdown.pointCount && { pointCount: points }),
            ...(charged !== null && charged !== initialCharged && { creditsCharged: charged }),
            ...(creditsWaived !== project.creditsWaived && { creditsWaived }),
          },
        },
      });
      onClose();
    } catch (error) {
      showToast({ title: failed, text: graphqlErrorCode(error) === 'NO_POINT_CLOUD' ? noPointCloud : error instanceof Error ? error.message : String(error), type: ToastType.ERROR });
    }
  }, [creditsCharged, creditsWaived, failed, initialCharged, noPointCloud, onClose, pointCount, project.creditBreakdown.pointCount, project.creditsWaived, project.id, setState, showToast, uploadStatus]);

  return (
    <Modal.Container open={open} onClose={onClose} panelClassName="w-full max-w-lg">
      <Modal.Header onClose={onClose}>
        <T _str="debug project state" swc />
      </Modal.Header>
      <Modal.Body className="flex flex-col gap-5 text-sm">
        <span className="text-gray-500">
          <T _str="Puts {name} in this state as it is, without charging or settling anything. What it overwrites stays in the audit log." args={{ name: project.name }} />
        </span>

        <div className="flex flex-col gap-2">
          <span className="font-medium">
            <T _str="status" swc />
          </span>
          <div className="flex flex-wrap gap-2">
            {STATUSES.map((status) => (
              <button
                key={status}
                type="button"
                onClick={() => setUploadStatus(status)}
                className={classNames(
                  'rounded-full border px-3 py-1 font-mono text-xs transition-colors',
                  status === uploadStatus ? 'border-neon-green-500 bg-neon-green-500 text-black' : 'border-gray-300 hover:border-neon-green-500 dark:border-gray-500',
                )}
              >
                {status}
              </button>
            ))}
          </div>
        </div>

        <div className="flex flex-col gap-2">
          <span className="font-medium">
            <T _str="point count" swc />
          </span>
          <div className="flex items-center gap-2">
            <Input className="grow font-mono" inputMode="numeric" value={pointCount} onChange={(event) => setPointCount(event.target.value)} />
            {POINT_PRESETS.map(({ label, points }) => (
              <Button key={label} variant="tertiary" size="xs" onClick={() => setPointCount(String(points))}>
                {label}
              </Button>
            ))}
          </div>
          <span className="text-xs text-gray-500">
            <T
              _str="Up to {limit} points is one project, above it two. Goes on the first converted point cloud (the first one is marked converted when none is)."
              args={{ limit: project.creditBreakdown.singleProjectMaxPoints.toLocaleString() }}
            />
          </span>
        </div>

        <div className="flex items-end gap-6">
          <label className="flex flex-col gap-2">
            <span className="font-medium">
              <T _str="projects already charged" swc />
            </span>
            <Input className="w-32 font-mono" inputMode="numeric" value={creditsCharged} onChange={(event) => setCreditsCharged(event.target.value)} />
          </label>
          <label className="flex items-center gap-2 pb-3">
            <CheckBox checked={creditsWaived} onChange={(event) => setCreditsWaived(event.target.checked)} />
            <T _str="free (waived)" swc />
          </label>
        </div>
      </Modal.Body>
      <Modal.Footer className="justify-end gap-3 text-base">
        <Button variant="secondary" size="sm" onClick={onClose}>
          <T _str="cancel" swc />
        </Button>
        <Button variant="superAdmin" size="sm" onClick={onApply} loading={loading}>
          <T _str="apply" swc />
        </Button>
      </Modal.Footer>
    </Modal.Container>
  );
};

export const ProjectDebugStateModal = memo(ProjectDebugStateModal_);
